import { useMemoizedFn } from 'ahooks';
import {
  getAvailableCoupons,
  getAvailableNexus,
  getCurrentUser,
  getMissionQuota,
  getUserTrialExpiration,
  getViralGrowthInfo,
} from '@/app/apis';
import { isEnterpriseDistribution, MAX_FREE_PROJECTS_COUNT, SITE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import { setRegisterPage } from '@/app/utils';
import { useFetch } from './use-fetch';

export const useUserInfo = () => {
  const {
    userInfo,
    updateUserInfo,
    isUserInfoInitialized,
    isSubscriptionInitialized,
    updateAvailableNexus,
    updatePresentPlanEffectiveTime,
    updateIsGiftPlan,
    updateAvailableCoupon,
    updateAgentInviteInfo,
    updateAdWatchCreditEligible,
  } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
    isUserInfoInitialized: state.isUserInfoInitialized,
    updateAvailableNexus: state.updateAvailableNexus,
    updatePresentPlanEffectiveTime: state.updatePresentPlanEffectiveTime,
    isSubscriptionInitialized: state.isSubscriptionInitialized,
    updateIsGiftPlan: state.updateIsGiftPlan,
    updateAvailableCoupon: state.updateAvailableCoupon,
    updateAgentInviteInfo: state.updateAgentInviteInfo,
    updateAdWatchCreditEligible: state.updateAdWatchCreditEligible,
  }));

  // 等 usersGetMe 有结果后再根据 userInfo 判断是否显示登录按钮
  const showLoginButton = isUserInfoInitialized && !userInfo;

  const { run: getUserInfo, runAsync: getUserInfoAsync } = useFetch(
    (_params?: { query: { is_first_login: boolean; skip_post_login_initialization?: boolean } }) => {
      return getCurrentUser();
    },
    {
      manual: true,
      suppressUnauthorizedLoginDialog: true,
      onSuccess: ({ data, error }, [params]) => {
        if (error) {
          updateUserInfo(null);
          return;
        }
        if (data) {
          updateUserInfo({ ...data, is_first_login: params?.query?.is_first_login || false });
          setRegisterPage({
            uid: data.id,
            is_member: data.subs_plan !== 'free',
            member_type: data.subs_plan,
          });
          if (!params?.query?.skip_post_login_initialization && !isEnterpriseDistribution) {
            fetchTrialExpiration();
          }
          if (!params?.query?.skip_post_login_initialization) {
            fetchAvailableNexusIfEnabled({
              query: { rule_set: ['sub_one_time_offer', 'mission_watch_ad_credit', 'claw_pass_bundle_first_purchase'] },
            });
          }
          // 已获取订阅信息，则更新是否在赠送套餐中
          if (isSubscriptionInitialized) {
            updateIsGiftPlan();
          }
          if (data.subs_plan === 'ultimate_v2') {
            getCouponAvailable({ query: { coupon_names: ['ultimate_v2_off_peak_coupon'] } });
          }
          if (!params?.query?.skip_post_login_initialization && !isEnterpriseDistribution) {
            // 获取agent邀请信息
            fetchAgentInviteInfo();
          }
        }
      },
    },
  );

  const { run: fetchTrialExpiration, runAsync: fetchTrialExpirationAsync } = useFetch(getUserTrialExpiration, {
    manual: true,
    onSuccess: ({ data }) => {
      if (data) {
        if (
          data.present_begin_at &&
          data.present_end_at &&
          new Date(data.present_end_at).getTime() < new Date('3000-01-01T00:00:00Z').getTime()
        ) {
          updatePresentPlanEffectiveTime([data.present_begin_at, data.present_end_at]);
        }
      }
    },
  });

  const fetchTrialExpirationIfEnabled = useMemoizedFn(() => {
    if (isEnterpriseDistribution) {
      return;
    }

    fetchTrialExpiration();
  });

  const fetchTrialExpirationAsyncIfEnabled = useMemoizedFn(async () => {
    if (isEnterpriseDistribution) {
      return undefined;
    }

    return fetchTrialExpirationAsync();
  });

  const { run: fetchAvailableNexus, runAsync: fetchAvailableNexusAsync } = useFetch(getAvailableNexus, {
    manual: true,
    onSuccess: ({ data }) => {
      // 无任何资格时后端不返回 rule_set，此时也要视为"已拉到"，
      // 否则等 nexus 就位的消费方（如开屏弹窗选窗）会一直等不到结果
      updateAvailableNexus(data?.rule_set ?? []);
      if (SITE === 'global' && data?.rule_set?.includes('mission_watch_ad_credit')) {
        fetchAdWatchCreditEligible({ query: { mission_type: 'watch_ad_credit' } });
      } else {
        updateAdWatchCreditEligible(false);
      }
    },
    // 拉取失败按"无资格"处理并标记就位，避免阻塞依赖方
    onError: () => {
      updateAvailableNexus([]);
      updateAdWatchCreditEligible(false);
    },
  });

  const resetAvailableNexus = useMemoizedFn(() => {
    updateAvailableNexus([]);
    updateAdWatchCreditEligible(false);
  });

  const fetchAvailableNexusIfEnabled = useMemoizedFn((...args: Parameters<typeof fetchAvailableNexus>) => {
    if (isEnterpriseDistribution) {
      resetAvailableNexus();
      return;
    }

    fetchAvailableNexus(...args);
  });

  const fetchAvailableNexusAsyncIfEnabled = useMemoizedFn(
    async (...args: Parameters<typeof fetchAvailableNexusAsync>) => {
      if (isEnterpriseDistribution) {
        resetAvailableNexus();
        return undefined;
      }

      return fetchAvailableNexusAsync(...args);
    },
  );

  const { run: fetchAdWatchCreditEligible } = useFetch(getMissionQuota, {
    manual: true,
    onSuccess: ({ data }) => {
      updateAdWatchCreditEligible(data?.eligible ?? false);
    },
  });

  // 判断该用户是否有指定优惠券
  const { run: getCouponAvailable } = useFetch(getAvailableCoupons, {
    manual: true,
    onSuccess: ({ data }) => {
      if (data) {
        updateAvailableCoupon(data.coupons?.map((v) => v.name ?? '') ?? []);
      }
    },
  });

  const { run: fetchAgentInviteInfo, runAsync: fetchAgentInviteInfoAsync } = useFetch(getViralGrowthInfo, {
    manual: true,
    onSuccess: ({ data }) => {
      if (data) {
        const max_friends_invited_count = (data.invites_per_project_unlock ?? 0) * MAX_FREE_PROJECTS_COUNT;
        const status = (data.friends_invited || 0) >= max_friends_invited_count ? 'not_invited' : 'invited';
        const free_projects_created = data.free_projects_created || 0; // 已创建的免费项目数量
        // 已获取的免费项目额度 = 邀请好友数量 / 每邀请x位好友获得1个免费项目
        const free_projects_quota = Math.min(
          Math.floor((data.friends_invited || 0) / (data.invites_per_project_unlock ?? 0)),
          MAX_FREE_PROJECTS_COUNT,
        );
        const has_free_project_count = free_projects_quota - free_projects_created; // 是否还有免费项目数量
        updateAgentInviteInfo({
          ...data,
          max_friends_invited_count,
          has_free_project_count,
          free_projects_quota,
          status,
        });
      }
    },
  });

  const fetchAgentInviteInfoIfEnabled = useMemoizedFn(() => {
    if (isEnterpriseDistribution) {
      return;
    }

    fetchAgentInviteInfo();
  });

  const fetchAgentInviteInfoAsyncIfEnabled = useMemoizedFn(async () => {
    if (isEnterpriseDistribution) {
      updateAgentInviteInfo(null);
      return undefined;
    }

    return fetchAgentInviteInfoAsync();
  });

  return {
    showLoginButton,
    getUserInfo,
    getUserInfoAsync,
    fetchTrialExpiration: fetchTrialExpirationIfEnabled,
    fetchTrialExpirationAsync: fetchTrialExpirationAsyncIfEnabled,
    fetchAvailableNexus: fetchAvailableNexusIfEnabled,
    fetchAvailableNexusAsync: fetchAvailableNexusAsyncIfEnabled,
    fetchAgentInviteInfo: fetchAgentInviteInfoIfEnabled,
    fetchAgentInviteInfoAsync: fetchAgentInviteInfoAsyncIfEnabled,
  };
};
